/**
 * SINQUE — motion engine.
 * GSAP + ScrollTrigger reveal animations and Lenis smooth scroll, ported from
 * the Astro client.ts. Uses the CDN globals loaded in theme.liquid:
 *   window.gsap, window.ScrollTrigger, window.Lenis
 *
 * MPA model: a standard Liquid theme does full-page navigations, so init runs on
 * every DOMContentLoaded. It also re-runs on 'shopify:section:load' (theme editor)
 * and is fully idempotent — prior ScrollTriggers are killed and any prior Lenis
 * instance is destroyed before re-init.
 *
 * Reduced motion: if the user prefers reduced motion we do NOT init Lenis or GSAP;
 * the CSS @media(prefers-reduced-motion) rule reveals .reveal-* content immediately.
 */
(function () {
  'use strict';

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var Lenis = window.Lenis;

  // ---------- module-scoped state ----------
  var lenis = null;
  var rafId = null;
  // Triggers we create directly (parallax) and want to track for teardown.
  // ScrollTrigger.getAll() covers everything including batch() triggers, so the
  // teardown path kills all of them; this array is kept for parity/clarity.
  var ownedTriggers = [];

  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function destroyMotion() {
    // Kill every ScrollTrigger (batch + parallax + header) so a re-init never
    // leaves stale triggers behind (theme editor section swaps, MPA re-entry).
    if (ScrollTrigger && ScrollTrigger.getAll) {
      ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
    }
    ownedTriggers = [];
    if (lenis) {
      try { lenis.destroy(); } catch (e) { /* noop */ }
      lenis = null;
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function initMotion() {
    // Guard: libraries must be present (CDN globals). If GSAP is missing there's
    // nothing to do — CSS still reveals content via the reduced-motion fallback
    // only, so force the reveal so the page is never stuck hidden.
    if (!gsap || !ScrollTrigger) {
      forceRevealAll();
      return;
    }

    // Always start from a clean slate (idempotent).
    destroyMotion();

    gsap.registerPlugin(ScrollTrigger);

    // Respect reduced-motion: no smooth scroll, no reveal/parallax animation.
    // CSS (@media prefers-reduced-motion) reveals .reveal-* elements immediately.
    if (reduceMotion()) {
      return;
    }

    // ---------- Lenis smooth scroll ----------
    if (Lenis) {
      lenis = new Lenis({
        duration: 1.05,
        easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true
      });

      // Drive Lenis from GSAP's ticker so scroll + animation share one clock.
      gsap.ticker.add(lenisTick);
      gsap.ticker.lagSmoothing(0);

      // Keep a raf fallback in case ticker is unavailable for any reason.
      // (ticker is the primary driver; this is belt-and-braces and harmless.)
      lenis.on('scroll', ScrollTrigger.update);
    }

    // ---------- Header scrolled state ----------
    ScrollTrigger.create({
      start: 60,
      end: 'max',
      onUpdate: function (self) {
        var header = document.getElementById('site-header');
        if (header) header.classList.toggle('is-scrolled', self.scroll() > 30);
      }
    });

    // ---------- Reveal animations ----------
    ScrollTrigger.batch('.reveal-up', {
      start: 'top 85%',
      onEnter: function (batch) {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.95,
          stagger: 0.08,
          ease: 'power3.out',
          overwrite: 'auto'
        });
      },
      once: true
    });

    ScrollTrigger.batch('.reveal-fade', {
      start: 'top 85%',
      onEnter: function (batch) {
        gsap.to(batch, {
          opacity: 1,
          duration: 1,
          stagger: 0.06,
          ease: 'power2.out',
          overwrite: 'auto'
        });
      },
      once: true
    });

    // ---------- Hero entrance ----------
    var heroHeadline = document.querySelector('.hero-headline, .drop-hero__name');
    if (heroHeadline) {
      gsap.from(heroHeadline, { opacity: 0, y: 36, duration: 1.2, ease: 'power3.out' });
    }
    var heroKicker = document.querySelector('.hero-kicker, .drop-hero__num-row');
    if (heroKicker) {
      gsap.from(heroKicker, { opacity: 0, y: 14, duration: 0.8, ease: 'power2.out', delay: 0.1 });
    }
    var heroSub = document.querySelector('.hero-sub, .drop-hero__tagline');
    if (heroSub) {
      gsap.from(heroSub, { opacity: 0, y: 14, duration: 0.9, ease: 'power2.out', delay: 0.25 });
    }

    // ---------- Hero visual gentle parallax ----------
    document.querySelectorAll('[data-hero-visual], .drop-hero__visual').forEach(function (el) {
      var inner = el.querySelector('.hero-tile-inner, .drop-hero__visual-inner');
      if (!inner) return;
      var trig = gsap.to(inner, {
        yPercent: -12,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6
        }
      });
      if (trig.scrollTrigger) ownedTriggers.push(trig.scrollTrigger);
    });

    // ---------- Drop placeholder hero number subtle parallax ----------
    document.querySelectorAll('.drop-placeholder--hero .dp-num').forEach(function (el) {
      var trig = gsap.to(el, {
        yPercent: -25,
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('.drop-placeholder'),
          start: 'top top',
          end: 'bottom top',
          scrub: 0.8
        }
      });
      if (trig.scrollTrigger) ownedTriggers.push(trig.scrollTrigger);
    });

    // ---------- About page mark — subtle settle ----------
    var aboutMark = document.querySelector('.about-mark');
    if (aboutMark) {
      gsap.from(aboutMark, { opacity: 0, y: 36, duration: 1.4, ease: 'power3.out' });
    }
    var aboutText = document.querySelector('.about-text');
    if (aboutText) {
      gsap.from(aboutText, { opacity: 0, y: 18, duration: 1.0, ease: 'power2.out', delay: 0.25 });
    }

    // ---------- Vault hero ----------
    var vaultH1 = document.querySelector('.vault-head h1');
    if (vaultH1) {
      gsap.from(vaultH1, { opacity: 0, y: 36, duration: 1.2, ease: 'power3.out' });
    }

    // Refresh ScrollTrigger after fonts load (font metrics shift layout).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        ScrollTrigger.refresh();
      });
    }
  }

  function lenisTick(time) {
    // gsap.ticker passes seconds; Lenis expects milliseconds.
    if (lenis) lenis.raf(time * 1000);
  }

  // If GSAP is entirely absent, make sure pre-state reveal elements still show.
  function forceRevealAll() {
    document.querySelectorAll('.reveal-up, .reveal-fade').forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  // ---------- Expose stop/start for overlays (ui.js calls these) ----------
  // ui.js prefers window.lenis directly; expose a tiny helper surface too.
  window.SinqueMotion = {
    getLenis: function () { return lenis; },
    stopScroll: function () { if (lenis) lenis.stop(); },
    startScroll: function () { if (lenis) lenis.start(); },
    refresh: function () { if (ScrollTrigger) ScrollTrigger.refresh(); }
  };

  // ---------- Lifecycle ----------
  function boot() {
    document.documentElement.classList.add('js');
    initMotion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init on section load; clean up on section unload.
  document.addEventListener('shopify:section:load', initMotion);
  document.addEventListener('shopify:section:unload', function () {
    // Recompute trigger positions after a section is removed.
    if (ScrollTrigger && ScrollTrigger.refresh) ScrollTrigger.refresh();
  });

  // Release scroll lock and tear down before the page unloads (safety for MPA).
  window.addEventListener('pagehide', function () {
    document.body.style.overflow = '';
    destroyMotion();
  });
})();
